import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/authContext";
import { Ticket, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { formatApiError } from "@/lib/api";

/**
 * FanPassportGate — authorization gate for the `/fan/*` passport shell.
 *
 * Fan Passport is a persistent, server-side account setting
 * (`user.has_fan_passport`). When an authenticated user without an
 * activated Fan Passport lands on /fan (directly, from a share link,
 * or from an old bookmark) we do NOT silently create one. Instead we
 * render an explicit activation screen so the choice remains the
 * user's — matching the "opt-in" contract set for Artist-primary
 * accounts.
 *
 * Behaviour:
 *   • user === null            → auth still loading; render a thin placeholder.
 *   • not authenticated        → bounce to /signin with a next= back to /fan.
 *   • has_fan_passport === true → render the passport (children).
 *   • has_fan_passport === false → render inline activation prompt.
 */
export default function FanPassportGate({ children }) {
    const { user, activateFanPassport } = useAuth();
    const location = useLocation();
    const [activating, setActivating] = useState(false);
    const [error, setError] = useState("");

    if (user === null) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center font-mono text-xs text-[#666]" data-testid="fan-gate-checking">
                Checking session…
            </div>
        );
    }
    if (!user) {
        const next = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/signin?next=${next}`} replace />;
    }
    if (user.has_fan_passport) {
        return children;
    }

    const activate = async () => {
        if (activating) return;
        setActivating(true);
        setError("");
        try {
            await activateFanPassport();
            toast.success("Fan Passport activated");
            // Children will render on next paint — user state now has flag=true.
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setActivating(false);
        }
    };

    return (
        <div className="max-w-xl mx-auto px-4 md:px-6 py-16" data-testid="fan-gate-activation-prompt">
            <div className="border border-[#2a2a2a] p-8">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Project Mint</div>
                <h1 className="mt-2 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">
                    Activate Fan Passport
                </h1>
                <p className="mt-3 text-sm text-[#a3a3a3]" data-testid="fan-gate-copy">
                    This account isn't set up as a fan yet. Fan Passport lets you follow artists and venues,
                    save events, and carry your ticket history across devices. You can activate it any time —
                    your other Project Mint workspaces stay untouched.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        onClick={activate}
                        disabled={activating}
                        className="pm-btn pm-btn-primary"
                        data-testid="fan-gate-activate-btn"
                    >
                        <Ticket className="w-4 h-4 mr-2" />
                        {activating ? "Activating…" : "Activate Fan Passport"}
                        {!activating && <ArrowRight className="w-3 h-3 ml-1 inline" />}
                    </button>
                    <a
                        href="/account"
                        className="pm-btn"
                        data-testid="fan-gate-account-link"
                    >
                        Go to Account settings
                    </a>
                </div>
                {error && (
                    <div className="mt-4 text-xs font-mono uppercase text-[#ff3333]" data-testid="fan-gate-error">
                        {error}
                    </div>
                )}
            </div>
        </div>
    );
}
